import React, { useContext, useCallback, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { GlobalContext } from '../GlobalContext'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Button from '../formfields/Button'
import Spinner from '../ui-elements/Spinner'
import RedAlert from '../ui-elements/RedAlert'
import Modal from '../ui-elements/Modal'
import { subscribe } from '../utils/pubsub'
import { useVideo } from '../context/video/VideoContext'
import { useVoice } from '../context/voice/VoiceContext'

// question types
import QuestionYesOrNo from './QuestionYesOrNo'
import QuestionText from './QuestionText'
import QuestionFridgeTemperature from './QuestionFridgeTemperature'
import QuestionPhoto from './QuestionPhoto'
import QuestionSelectMultiple from './QuestionSelectMultiple'
import QuestionSelectOne from './QuestionSelectOne'
import { b64toBlob } from '../utils/b64toBlob'
import QuestionConfirmPage from './QuestionConfirmPage'
import { findNextQuestionUrl } from '../utils/findNextQuestionUrl'
import { getChecklistProgress } from '../utils/getChecklistProgress'
import { checkIfAnswerIsLocked } from '../utils/checkIfAnswerIsLocked'
import Card from '../ui-elements/Card'
import QuestionNumber from './QuestionNumber'
import QuestionMultipleTextFields from './QuestionMultipleTextFields'
import HeaderDetails from '../shared-components/HeaderDetails'
import { fridgeName } from '../utils/fridgeName'
import { getAssetIdFromUrl } from '../utils/getAssetIdFromUrl'
import QuestionPhotoAndReading from './QuestionPhotoAndReading'
import QuestionCustomFormat from './QuestionCustomFormat'
import QuestionDateAndTime from './QuestionDateAndTime'
import QuestionDate from './QuestionDate'
import QuestionTime from './QuestionTime'
import JsonDebug from '../ui-elements/JsonDebug'
import QuestionMultipleFields from './QuestionMultipleFields'
import QuestionSignature from './QuestionSignature'
import AiAnswer from './AiAnswer'
import FormatCheckPeriodInProgress from '../dashboard/FormatCheckPeriodInProgress'
import QuestionLocation from './QuestionLocation'
import tryToInterpretNumber from '../utils/tryToInterpretNumber'
import tryToInterpretDate from '../utils/tryToInterpretDate'
import { questionOptionsForSelectMultipleThatMatchSpokenAnswer } from '../utils/questionOptionsForSelectMultipleThatMatchSpokenAnswer'
import { questionOptionForSelectOneThatMatchesSpokenAnswer } from '../utils/questionOptionForSelectOneThatMatchesSpokenAnswer'
import { updateMultipleTextFields } from '../utils/updateMultipleTextFields'
import IsListening from '../ui-elements/IsListening'
import { useOrientation } from '../context/orientation/OrientationContext'


type Props = {
  showAnswerConfirmation: boolean
  children?: any
}

function QuestionPage({
  showAnswerConfirmation = false,
  children
}: Props) {
  const {
    tableData,
    showModal,
    setShowModal,
    sendMessageToWebsocket,
    isVideoMode,
    setIsVideoMode,
    setIsVideoPlaying,
    loggedIn,
    userData,
    checkIfThereAreQuestionsAndAnswersForChecklistPeriodId,
    fetchQuestionsAndAssetsForChecklistId,
    t,
    isRTL
  } = useContext(GlobalContext)

  const {
    isListeningEnabled,
    registerCommands,
    isRecognizerRunning,
    voiceCommand,
    clearVoiceCommand,
    clearSpokenAnswer,
    questionTypesIncompatibleWithVoiceMode,
    spokenAnswer,
    finalSpokenAnswer,
    startListening,
    stopListening,
    registeredCommands
  } = useVoice()

  const { i18n } = useTranslation()

  const navigate = useNavigate()
  const { answerId, checkPeriodId } = useParams()
  const [isQuestionLocked, setIsQuestionLocked] = useState(false)
  const [lockedUntilText, setLockedUntilText] = useState('')
  const [isAnswerPending, setIsAnswerPending] = useState(false)
  const [thisQuestion, setThisQuestion] = useState<null | ObjectStringKeyAnyValue>(null)
  const [questionAnswerValue, setQuestionAnswerValue] = useState<null | string | ObjectStringKeyAnyValue | Array<string>>(null)
  const [questionAnswerQRProof, setQuestionAnswerQRProof] = useState<null | string>(null)
  const [questionAnswer, setQuestionAnswer] = useState<null | ObjectStringKeyAnyValue>(null)
  const [answerNote, setAnswerNote] = useState<null | string>(null)
  const [answerPhoto, setAnswerPhoto] = useState<null | string>(null)
  // const [titleText, setTitleText] = useState<string>('')
  const [subheadingText, setSubheadingText] = useState<string>('')
  const [isAnswerManuallyEdited, setIsAnswerManuallyEdited] = useState(false)
  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const [nextButtonText, setNextButtonText] = useState('')
  const [nextButtonAction, setNextButtonAction] = useState<null | AnyFunction>(null)
  const [nextStepUrl, setNextStepUrl] = useState<null | string>(null)
  const [checklistProgress, setChecklistProgress] = useState<null | ObjectStringKeyAnyValue>(null)
  const [jpegString, setJpegString] = useState<string | null>(null)
  const { captureAsJPEGString, QRCodesPresent, setQRCodesPresent, clearCameraStateVariables } = useVideo()
  const [fridgeHasDigitalDisplay, setFridgeHasDigitalDisplay] = useState<null | boolean>(null)
  const [footerButtons, setFooterButtons] = useState<Array<string>>(['back'])
  const [doesScannedQrCodeMatchAssetId, setDoesScannedQrCodeMatchAssetId] = useState(false)
  const [showAiAnswerModal, setShowAiAnswerModal] = useState(true)
  const [lastMultiTextFieldFromSpeech, setLastMultiTextFieldFromSpeech] = useState<string | null>(null)
  const backButtonRef = useRef<any>(null)
  const nextButtonRef = useRef<any>(null)

  const checklistId = checkPeriodId && tableData && tableData.ChecklistPeriods && tableData.ChecklistPeriods[checkPeriodId] && tableData.ChecklistPeriods[checkPeriodId]['ChecklistId'] ? tableData.ChecklistPeriods[checkPeriodId]['ChecklistId'] : null
  const thisCheckPeriodSignoff = checkPeriodId && tableData && tableData.ChecklistPeriods && tableData.ChecklistPeriods[checkPeriodId] && tableData.ChecklistPeriods[checkPeriodId]['Signoff'] ? tableData.ChecklistPeriods[checkPeriodId]['Signoff'] : null

  const thisCheckPeriod = checkPeriodId && tableData && tableData.ChecklistPeriods && tableData.ChecklistPeriods[checkPeriodId] ? tableData.ChecklistPeriods[checkPeriodId] : null

  const processIdForThisChecklist = thisCheckPeriod && thisCheckPeriod.ProcessDefinitionId ? thisCheckPeriod.ProcessDefinitionId : null

  const regexName = thisQuestion && thisQuestion.CustomDataFormatId && tableData.CustomDataFormats && tableData.CustomDataFormats[thisQuestion.CustomDataFormatId].RegexName

  const regexString = thisQuestion && thisQuestion.CustomDataFormatId && tableData.CustomDataFormats && tableData.CustomDataFormats[thisQuestion.CustomDataFormatId].RegexString



  useEffect(() => {
    // when page loads, run startlistening
    if (isListeningEnabled) {
      startListening()
    }

    // when leaving the page, stop listening and clear spoken answer
    return () => {
      stopListening('🗒️')
      clearSpokenAnswer()
    }
  }, [isListeningEnabled])

  useEffect(() => {
    // when answer id changes, reset setShowAiAnswerModal to true
    setShowAiAnswerModal(true)
  }, [answerId])

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [errorMessage])

  useEffect(() => {
    // check if it's locked

    if (questionAnswer) {
      checkIfAnswerIsLocked(questionAnswer, setIsQuestionLocked, setLockedUntilText)
      const intervalId = setInterval(() => {
        checkIfAnswerIsLocked(questionAnswer, setIsQuestionLocked, setLockedUntilText)
      }, 1000)
      return () => clearInterval(intervalId)
    }
  }, [questionAnswer])


  useEffect(() => {
    checklistId && fetchQuestionsAndAssetsForChecklistId(checklistId)
  }, [checklistId])

  useEffect(() => {
    if (
      doesScannedQrCodeMatchAssetId
      && thisQuestion
      && thisQuestion.QuestionFormat === 'PhotoAndReading'
    ) {
      const newFooterButtons = [...footerButtons].filter(button => button !== 'takePhoto')
      setFooterButtons([...newFooterButtons, 'saveWithNote', 'save', 'retakePhoto'])
    }

  }, [jpegString])

  useEffect(() => {
    handleSetThisQuestion()

    if (tableData && checkPeriodId) {
      checkIfThereAreQuestionsAndAnswersForChecklistPeriodId(checkPeriodId)
      const progress = getChecklistProgress(checkPeriodId, tableData)
      if (progress) {
        setChecklistProgress(progress)
      }
    }

    if (thisQuestion && checkPeriodId && tableData) {
      const nextStep = findNextQuestionUrl(answerId, checkPeriodId, tableData, '')
      nextStep && setNextStepUrl(nextStep)
    }


  }, [tableData, answerId, thisQuestion, checkPeriodId])



  useEffect(() => {
    // clear error message when answer gets updated
    setErrorMessage(null)
  }, [questionAnswerValue])

  useEffect(() => {
    // set the footer buttons
    displayFooterButtons()

  }, [questionAnswer, questionAnswerValue, thisQuestion])

  useEffect(() => {

    if (spokenAnswer !== '') {
      setShowModal({ heardVoiceAnswer: spokenAnswer })
    }

  }, [finalSpokenAnswer, spokenAnswer, thisQuestion])



  useEffect(() => {
    const lowerSpokenAnswer = spokenAnswer.toLowerCase()
    const lowerFinalSpokenAnswer = finalSpokenAnswer.toLowerCase()


    if (thisQuestion) {


      if (thisQuestion.QuestionFormat === 'YesOrNo') {
        if (lowerSpokenAnswer.includes('yes')) {
          if (questionAnswerValue !== 'Yes') {
            setQuestionAnswerValue('Yes')
          }
        } else if (lowerSpokenAnswer.includes('no')) {
          if (questionAnswerValue !== 'No') {
            setQuestionAnswerValue('No')
          }
        }
      } else if (thisQuestion.QuestionFormat === 'Number') {
        const interpretedNumber = tryToInterpretNumber(spokenAnswer)
        if (questionAnswerValue !== interpretedNumber && interpretedNumber.trim() !== '') {
          setQuestionAnswerValue(interpretedNumber)
        }
      } else if (
        thisQuestion.QuestionFormat === 'Photo' ||
        thisQuestion.QuestionFormat === 'PhotoAndReading'
      ) {
        if (
          lowerSpokenAnswer.includes('take photo') ||
          lowerSpokenAnswer.includes('take picture') ||
          lowerSpokenAnswer.includes('capture')
        ) {
          capturePhoto()
        }
      } else if (thisQuestion.QuestionFormat === 'Date') {
        const dateSpoken = tryToInterpretDate(spokenAnswer)
        if (questionAnswerValue !== dateSpoken && dateSpoken.trim() !== '') {
          setQuestionAnswerValue(dateSpoken)
        }
      } else if (thisQuestion.QuestionFormat === 'DateAndTime') {
        const dateSpoken = tryToInterpretDate(spokenAnswer, true)
        if (questionAnswerValue !== dateSpoken && dateSpoken.trim() !== '') {
          setQuestionAnswerValue(dateSpoken)
        }
      } else if (thisQuestion.QuestionFormat === 'SelectMultiple') {
        const optionsFromSpokenText = questionOptionsForSelectMultipleThatMatchSpokenAnswer(questionAnswerValue, spokenAnswer, thisQuestion.QuestionOptions)
        if (optionsFromSpokenText.length > 0) {
          setQuestionAnswerValue(optionsFromSpokenText)
        }
      } else if (thisQuestion.QuestionFormat === 'SelectOne') {
        const optionsFromSpokenText = questionOptionForSelectOneThatMatchesSpokenAnswer(spokenAnswer, thisQuestion.QuestionOptions)
        if (optionsFromSpokenText) {
          setQuestionAnswerValue(optionsFromSpokenText)
        }
      } else if (
        thisQuestion.QuestionFormat === 'MultipleTextFields'
      ) {
        const updatedFieldsAndAnswers = updateMultipleTextFields(questionAnswerValue, lowerSpokenAnswer, lowerFinalSpokenAnswer, thisQuestion.QuestionFieldNames, lastMultiTextFieldFromSpeech, setLastMultiTextFieldFromSpeech)
        setQuestionAnswerValue(updatedFieldsAndAnswers)

      } else if (
        thisQuestion.QuestionFormat === 'Text' ||
        thisQuestion.QuestionFormat === 'CustomFormat'
      ) {
        if (questionAnswerValue !== lowerFinalSpokenAnswer && lowerFinalSpokenAnswer.trim() !== '') {
          setQuestionAnswerValue(lowerFinalSpokenAnswer)
        }
      }
      else if (!questionTypesIncompatibleWithVoiceMode.includes(thisQuestion.QuestionFormat)) {
        setQuestionAnswerValue(spokenAnswer)
      }
    }
  }, [finalSpokenAnswer, thisQuestion])

  // figure out device orientation when taking photos
  const { orientation, windowOrientation, enableOrientation, orientationPermissionStatus } = useOrientation()
  const [capturedOrientation, setCapturedOrientation] = useState<string | null>(null)

  useEffect(() => {
    if (
      thisQuestion &&
      (thisQuestion.QuestionFormat == 'Photo' ||
        thisQuestion.QuestionFormat == 'FridgeTemperature' ||
        thisQuestion.QuestionFormat == 'PhotoAndReading') &&
      orientationPermissionStatus === 'not_requested'
    ) {
      // Note: on iOS 13+ this must ideally be called from a user gesture.
      // Calling here works when the navigation itself was a tap, but can be
      // rejected if the camera permission dialog is still open at the same time.
      enableOrientation()
    }
  }, [thisQuestion, orientationPermissionStatus])



  const displayFooterButtons = () => {
    if (thisQuestion &&
      thisQuestion.QuestionFormat !== 'Photo' &&
      thisQuestion.QuestionFormat !== 'FridgeTemperature' &&
      thisQuestion.QuestionFormat !== 'PhotoAndReading'
    ) {
      if (questionAnswerValue && questionAnswer && questionAnswer.Pending === true) {
        setFooterButtons(['back', 'saveWithNote', 'save'])
      }
      else if (questionAnswer && questionAnswer.Pending === false) {
        // console.log('🥝')
        setFooterButtons(['back', 'next'])
      } else {
        if (thisQuestion && thisQuestion.isAnswerRequired === false) {
          setFooterButtons(['skip', 'back'])
        } else {
          setFooterButtons(['back'])
        }
      }
    }
  }






  const handleSetThisQuestion = () => {
    if (
      answerId &&
      tableData &&
      tableData.ChecklistQuestions &&
      tableData.ChecklistAnswers
    ) {

      if (tableData.ChecklistAnswers[answerId]) {
        let subtitleTextString = ''
        const questionAnswerObject = tableData.ChecklistAnswers[answerId]
        setQuestionAnswer(questionAnswerObject)
        //console.log("Setting question answer in question page because handleSetThisQuestion was called")
        questionAnswerObject.Answer && setQuestionAnswerValue(questionAnswerObject.Answer)
        setIsAnswerPending(questionAnswerObject.Pending)
        const questionIdForThisAnswer = questionAnswerObject.QuestionId
        if (tableData.ChecklistQuestions[questionIdForThisAnswer]) {
          const questionForThisAnswer = tableData.ChecklistQuestions[questionIdForThisAnswer]
          setThisQuestion(questionForThisAnswer)

          // if it's a fridge temperature question, check if it's got a digital display
          if (questionForThisAnswer.FridgeId) {
            if (
              tableData.Assets &&
              tableData.Assets[questionForThisAnswer.FridgeId] &&
              tableData.Assets[questionForThisAnswer.FridgeId].HasDigitalReadout === true
            ) {
              //console.log(tableData.Assets[questionForThisAnswer.FridgeId])
              setFridgeHasDigitalDisplay(true)
            } else {
              setFridgeHasDigitalDisplay(false)
            }

            // set the subtitle text
            if (QRCodesPresent && QRCodesPresent.length > 0) {
              let assetIdFromDataUrl = getAssetIdFromUrl(QRCodesPresent[0])
              if (assetIdFromDataUrl === questionForThisAnswer.FridgeId) {
                setDoesScannedQrCodeMatchAssetId(true)
                if (questionForThisAnswer.QuestionFormat === 'PhotoAndReading') {
                  subtitleTextString = t('QuestionPages.QuestionPage.qrCodeDetectedTakePhoto')
                } else {
                  subtitleTextString = t('QuestionPages.QuestionPage.qrCodeDetected')
                }
              }
            }
          }


          // set the subheading text if its a follow up
          if (questionForThisAnswer.parentQuestionId && tableData.ChecklistQuestions[questionForThisAnswer.parentQuestionId]) {
            const parentQuestionText = tableData.ChecklistQuestions[questionForThisAnswer.parentQuestionId].QuestionText
            subtitleTextString = `${subtitleTextString} ${t('QuestionPages.QuestionPage.followUpQuestionFor')} ${parentQuestionText ? `"${parentQuestionText}"` : `ID #:${questionForThisAnswer.parentQuestionId}`}`
          }
        }

        // set the subheading text if its a repeated
        if (questionAnswerObject.repeatAnswerAfter && questionAnswerObject.repeatAnswerAfter.type === 'numberOfTimes') {
          subtitleTextString = `${subtitleTextString} ${t('QuestionPages.QuestionPage.sample', { sampleNumber: parseInt(questionAnswerObject.repeatAnswerAfter.number) + 1 })}`
        }

        // set the subheading text if its a delayed question
        if (questionAnswerObject.repeatAnswerAfter && questionAnswerObject.repeatAnswerAfter.type === 'timeDelay') {
          subtitleTextString = `${subtitleTextString} ${t('QuestionPages.QuestionPage.samplingAfter')}: 
          
          ${questionAnswerObject.repeatAnswerAfter.number < 60 ? 
            ` ${t('QuestionPages.QuestionPage.repeatAfterMinutes', { minutes: questionAnswerObject.repeatAnswerAfter.number })} ` : 
            ` ${t('QuestionPages.QuestionPage.repeatAfterHours', { hours: Math.round(questionAnswerObject.repeatAnswerAfter.number / 60 * 10) / 10 })}
             `}`
        }

        // set the title text string
        let titleTextString = thisQuestion ? thisQuestion.QuestionText : t('QuestionPages.QuestionPage.unknownQuestion')
        if (thisQuestion && thisQuestion.FridgeId) {
          if (thisQuestion.QuestionFormat === 'PhotoAndReading') {
            titleTextString = `${fridgeName(thisQuestion.FridgeId, tableData)}`
          } else {
            const assetName = fridgeName(thisQuestion.FridgeId, tableData)
            titleTextString = t('QuestionPages.QuestionPage.tempCheckForAssetName', { assetName })
          }
        }

        // if a custom data format is required, add it to the subtitle text
        if (thisQuestion && regexName) {
          subtitleTextString = `${subtitleTextString} ${t('QuestionPages.QuestionPage.requiredDataFormat', { regexName })}`
        }


        // setTitleText(titleTextString)
        setSubheadingText(subtitleTextString)
      }
    }
  }


  const handleChangeSelectMultiple = (value: string) => {
    let newAnswerArray: string[]

    // if existing questionAnswer is not an array, start a new array
    if (questionAnswerValue && questionAnswerValue instanceof Array) {
      newAnswerArray = [...questionAnswerValue]
    } else {
      newAnswerArray = []
    }
    // add the value if it doesn't already exist
    // remove the value if it does exist already
    if (newAnswerArray.includes(value)) {
      newAnswerArray = newAnswerArray.filter((option) => option !== value)
    } else {
      newAnswerArray.push(value)
    }
    setQuestionAnswerValue(newAnswerArray)
  }


  const handleChangeMultiText = (key: string, value: string) => {

    let newAnswerObject: ObjectStringKeyAnyValue = {}
    if (questionAnswerValue && questionAnswerValue instanceof Object) {
      newAnswerObject = { ...questionAnswerValue }
    }
    if (value) {
      newAnswerObject[key] = value
    } else {
      delete newAnswerObject[key]
    }


    setQuestionAnswerValue(newAnswerObject)
  }


  const validate = async (behaviour: any) => {
    // console.log(`Validating answer for question id: ${thisQuestion?.Id} with value: ${questionAnswerValue}`)
    let isValid = true

    if (!questionAnswerValue && thisQuestion?.QuestionFormat !== 'Photo') {
      setErrorMessage(t('QuestionPages.QuestionPage.errorPleaseSelect'))
    }

    if (questionAnswerValue && thisQuestion && regexString) {
      let emailRegex = new RegExp(regexString)
      let doesAnswerMatchPattern = emailRegex.test(`${questionAnswerValue}`)
      if (!doesAnswerMatchPattern) {
        isValid = false
        const regexText = regexName ? ` (${regexName})` : ''
      setErrorMessage(t('QuestionPages.QuestionPage.errorInvalidFormat', { regexText }))
    }
  }


    if (questionAnswerValue && thisQuestion?.QuestionFormat === 'Number') {
      const allowedValues = thisQuestion.AllowedValues
      const decimalPlaces = thisQuestion.DecimalPlaces ? parseInt(`${thisQuestion.DecimalPlaces}`) : null

      if (allowedValues === 'onlyNegative' && parseInt(`${questionAnswerValue}`) >= 0) {
        isValid = false
        setErrorMessage(t('QuestionPages.QuestionPage.errorNegativeNumbersOnly'))
      } else if (allowedValues === 'onlyPositive' && parseInt(`${questionAnswerValue}`) < 0) {
        isValid = false
        setErrorMessage(t('QuestionPages.QuestionPage.errorPositiveNumbersOnly'))
      }

      if (decimalPlaces !== null) {
        const decimalParts = `${questionAnswerValue}`.split('.')
        console.log(decimalParts)
        if (
          // if expected decimal places is more than zero, there must be at least one digit after .
          (decimalPlaces > 0 && decimalParts.length === 1) || 
          // if there are decimal places, they must match the expected length
          (decimalParts[1] && decimalParts[1].length !== decimalPlaces)) {
          isValid = false
          setErrorMessage(t('QuestionPages.QuestionPage.errorDecimalPlaces', { decimalPlaces }))
        }
      }


    }

    if (
      thisQuestion?.QuestionFormat === 'MultipleTextFields' &&
      (!questionAnswerValue ||
        Object.keys(questionAnswerValue).length === 0 ||
        Object.keys(questionAnswerValue).length < thisQuestion.QuestionFieldNames.length
      )
    ) {
      isValid = false
      setErrorMessage(t('QuestionPages.QuestionPage.errorPleaseFillAllFields'))
    }

    if (thisQuestion?.QuestionFormat === 'Photo' && !answerPhoto) {
      isValid = false
      setErrorMessage(t('QuestionPages.QuestionPage.errorPleaseTakeAPicture'))
    }

    if (isValid) {
      //console.log(`✅ validating answer for question id: ${thisQuestion?.Id}`)
      await saveAnswer(behaviour)
    }
  }

  const saveNote = async () => {
    //console.log(`saving note "${answerNote}" for answer id: ${answerId}`)
    if (answerId && answerNote) {
      setShowModal({ "spinner": t('General.saving') })
      const payload: ObjectStringKeyAnyValue = {
        action: "checklistsClientApp",
        subAction: "saveAnswerNote",
        answerId: `${answerId}`,
        answerNote: answerNote
      }
      // if a user is logged in as a guest, add their name to the data
      if (loggedIn === 'guest') {
        payload['GuestUserName'] = userData.name
      }
      const unsubscribe = subscribe("tableUpdateItemModified", data => {
        if (data.newRecord && data.newRecord.Id === answerId) {
          setShowModal(null)
          setAnswerNote(null)
          unsubscribe()
        }
      })
      sendMessageToWebsocket(JSON.stringify(payload))
    }
  }

  const saveAnswer = async (behaviour: any) => {
    // console.log(`Saving answer for question id: ${thisQuestion?.Id} with value: ${questionAnswerValue}`)
    setErrorMessage(null)
    if (answerId) {
      setFooterButtons(['back'])
      setQRCodesPresent(null)
      setShowModal({ "spinner": t('General.saving') })

      // send answer to websocket
      const answerPayload: ObjectStringKeyAnyValue = {
        action: "checklistsClientApp",
        subAction: "saveAnswerToQuestion",
        answerId: `${answerId}`,
        userLanguage: i18n.language,
        values: {}
      }

      // add the answer value if there is one
      if (questionAnswerValue) {
        answerPayload.values['Answer'] = questionAnswerValue
      }

      // if its a skipped question, add this flag to the answer object
      if (behaviour === 'skip') {
        answerPayload.values['AnswerSkipped'] = true
      }

      // add an answer note if there is one
      if (answerNote) answerPayload.values['AnswerNote'] = answerNote

      // if a user is logged in as a guest, add their name to the data
      if (loggedIn === 'guest') {
        answerPayload['GuestUserName'] = userData.name
      }

      // if its a fridge temp, include if it's manually edited
      if (thisQuestion?.QuestionFormat === 'FridgeTemperature' || thisQuestion?.QuestionFormat === 'PhotoAndReading') {
        answerPayload.values['isAnswerManuallyEdited'] = isAnswerManuallyEdited
      }

      // for photo questions, save orientation metadata to help diagnose upside-down photos
      if (
        thisQuestion?.QuestionFormat === 'Photo' ||
        thisQuestion?.QuestionFormat === 'FridgeTemperature' ||
        thisQuestion?.QuestionFormat === 'PhotoAndReading'
      ) {
        if (capturedOrientation !== null) {
          answerPayload.values['PhotoCapturedOrientation'] = capturedOrientation
        }
        answerPayload.values['OrientationPermissionStatus'] = orientationPermissionStatus
      }


      // navigate once answer is saved

      // if there is a photo, upload it
      if (answerPhoto) {
        const timestamp = `${Date.now()}`
        let fileName = `${answerId}-${timestamp}.jpg`
        await handleImageUpload(answerId, answerPhoto, fileName, timestamp, behaviour)
      }
      if (questionAnswerQRProof) {
        const timestamp = `${Date.now()}`
        let fileName = `${answerId}-qrproof-${timestamp}.jpg`
        await handleImageUpload(answerId, questionAnswerQRProof, fileName, timestamp, behaviour, 'QRProofFileName')
      }

      // console.log(payload)
      // navigate once answer is saved
      const unsubscribe = subscribe("answerUpdateComplete", data => {
        if (data.answerId === answerId) {
          unsubscribe()
          setShowModal(null)
          setJpegString(null)
          setQuestionAnswerValue(null)
          setAnswerNote(null)
          setAnswerPhoto(null)
          const nextUrl = (findNextQuestionUrl(answerId, checkPeriodId, tableData, behaviour))
          navigate(nextUrl)
        }
      })
      sendMessageToWebsocket(JSON.stringify(answerPayload))

    } else {
      setErrorMessage(t('QuestionPages.QuestionPage.errorCouldNotSave'))
    }
  }


  const handleImageUpload = async (answerId: string, dataString: string, fileName: string, timestamp: string, behaviour: any, image_key = "ImageFileName") => {
    const payload = JSON.stringify({
      action: "checklistsClientApp",
      subAction: "requestPresignedUrl",
      fileName: fileName,
      waitingForUploadUrlId: timestamp
    })
    const response = new Promise<void>((resolve, reject) => {
      const unsubscribe = subscribe("returnPresignedUrl", async data => {
        if (data.waitingForUploadUrlId === timestamp) {
          unsubscribe()
          const prefix = 'data:image/jpeg;base64,'
          const fileBody = dataString!.split(prefix)[1]
          const file = new File([b64toBlob(fileBody)], timestamp, { type: "image/jpeg" })
          await fetch(data.signedUrl, {
            method: 'PUT',
            body: file
          })
            .then(() => {
              // send answer to websocket
              let payload_obj: ObjectStringKeyAnyValue = {
                action: "checklistsClientApp",
                subAction: "saveAnswerToQuestion",
                answerId: `${answerId}`,
                userLanguage: i18n.language,
              }
              payload_obj.values = {}
              payload_obj.values[`${image_key}`] = fileName
              const payload = JSON.stringify(payload_obj)
              sendMessageToWebsocket(payload)
              resolve()
            }).catch((error) => {
              console.error('Error:', error)
            })
        }
      })
    })
    sendMessageToWebsocket(payload)
    await response
  }

  const retakePhoto = () => {
    setAnswerPhoto && setAnswerPhoto(null)
    setJpegString(null)
    setCapturedOrientation(null)
    //console.log("Retake photo: turning video on 2")
    setIsVideoPlaying(true)
    setIsVideoMode(true)
    setQRCodesPresent(null)
    clearCameraStateVariables()
    setQuestionAnswerValue(null)
  }


  const capturePhoto = () => {
    // If sensor orientation isn't available (iOS permission not granted / not yet received),
    // fall back to windowOrientation which is derived from screen.orientation.angle and
    // needs no permission. This handles the most common upside-down / sideways cases.
    const effectiveOrientation = orientation !== 'unknown' ? orientation : windowOrientation
    const jpgData = captureAsJPEGString(800, effectiveOrientation)
    setAnswerPhoto && setAnswerPhoto(jpgData || null)
    setJpegString && setJpegString(jpgData || null)
    setCapturedOrientation(effectiveOrientation)
    setIsVideoPlaying(false)
    setIsVideoMode(false)
  }



  const resetQuestion = (answerId: string) => {
    setShowModal({ "spinner": t('General.saving') })
    const payload = JSON.stringify({
      action: "checklistsClientApp",
      subAction: "resetAnswer",
      answerId: answerId
    })
    const unsubscribe = subscribe("tableUpdateItemModified", data => {
      //console.log(`🪼 ${JSON.stringify(data)}`)
      if (data.newRecord && data.newRecord.Id && data.newRecord.Id === answerId) {
        setShowModal(null)
        navigate(`/checklists/${checkPeriodId}`)
        unsubscribe()
      }
    })
    sendMessageToWebsocket(payload)
  }

  // Add a ref to store the latest value of questionAnswerValue




  // Register commands on component mount
  useEffect(() => {
    registerCommands(['save', 'safe', 'say', 'note', 'capture', 'retake', 'back', 'reset', 'next'])
  }, [])

  // Respond to voice commands
  useEffect(() => {
    if (voiceCommand === 'save' || voiceCommand === 'safe' || voiceCommand === 'say') {
      console.log('Save command recognized')
      if (footerButtons.includes('save')) {
        validate(null)
        clearSpokenAnswer()
        setShowModal({ "heardVoiceCommand": 'save' })
      } else {
        console.log('Save command recognized but save button is not available')
      }
    } else if (voiceCommand === 'note') {
      if (footerButtons.includes('saveWithNote')) {
        console.log('Note command recognized')
        validate('confirm')
        clearSpokenAnswer()
        setShowModal({ "heardVoiceCommand": voiceCommand })
      } else {
        console.log('Note command recognized but save with note button is not available')
      }

    } else if (voiceCommand === 'capture') {
      console.log('Capture command recognized')
      setShowModal({ "heardVoiceCommand": voiceCommand })
      capturePhoto()
    }
    else if (voiceCommand === 'back') {
      setShowModal({ "heardVoiceCommand": voiceCommand })
      console.log('Clicking back')
      backButtonRef.current.click()
    }
    else if (voiceCommand === 'reset') {
      setShowModal({ "heardVoiceCommand": voiceCommand })
      console.log('⌫ Resetting answer values')
      setQuestionAnswerValue(null)
    }
    else if (voiceCommand === 'retake') {
      setShowModal({ "heardVoiceCommand": voiceCommand })
      retakePhoto()
    }
    else if (voiceCommand === 'accept') {
      setShowModal({ "heardVoiceCommand": voiceCommand })
      const acceptButton = document.getElementById('ai-answer-accept')
      acceptButton && acceptButton.click()
    }
    else if (voiceCommand === 'cancel') {
      setShowModal({ "heardVoiceCommand": voiceCommand })
      const cancelButton = document.getElementById('ai-answer-cancel')
      cancelButton && cancelButton.click()
    }
    clearVoiceCommand()
  }, [voiceCommand, questionAnswerValue])

  // respond to 'next' command
  useEffect(() => {
    if (voiceCommand === 'next') {
      console.log('🤖')
      setShowModal({ "heardVoiceCommand": voiceCommand })
      clearVoiceCommand()

      if (!showAnswerConfirmation && thisQuestion && thisQuestion.QuestionFormat === 'MultipleTextFields') {
        const questionFieldNames = thisQuestion.QuestionFieldNames
        const indexOfLastTextFieldFromSpeech = questionFieldNames.indexOf(lastMultiTextFieldFromSpeech)
        const numberOfFieldNames = questionFieldNames.length
        console.log(`indexOfLastTextFieldFromSpeech: ${indexOfLastTextFieldFromSpeech}`)
        const newIndex = indexOfLastTextFieldFromSpeech + 1 < numberOfFieldNames ? indexOfLastTextFieldFromSpeech + 1 : 0
        setLastMultiTextFieldFromSpeech(questionFieldNames[newIndex])
      }
      else {
        setShowModal({ "heardVoiceCommand": voiceCommand })
        console.log('Clicking next ⏭️')
        nextButtonRef && nextButtonRef.current && nextButtonRef.current.click()
      }

    }
  }, [voiceCommand, thisQuestion, lastMultiTextFieldFromSpeech])





  return <div
    className={`w-full flex-1 h-full flex flex-col justify-between gap-2`}
  >


    <div className={`w-full flex flex-col items-center`}>

      <HeaderDetails
        instructionsText={thisQuestion && thisQuestion.QuestionInstructions}
        subheadingText={subheadingText}
        thisQuestion={thisQuestion}
        isAnswerPending={isAnswerPending}
      />





      {tableData && thisQuestion ?
        <div className={`max-w-3xl w-full p-5 flex flex-col gap-2 items-center justify-center text-center`}>


          <FormatCheckPeriodInProgress
            thisChecklistPeriod={thisCheckPeriod}
          />


          {!thisQuestion && <div>
            <RedAlert>{t('QuestionPages.QuestionPage.invalidQuestion')}</RedAlert>
          </div>}


          {thisQuestion && !isAnswerPending && showAnswerConfirmation &&
            <QuestionConfirmPage
              question={thisQuestion}
              answerId={answerId}
              answerNote={answerNote}
              setAnswerNote={setAnswerNote}
              saveNote={saveNote}
              setJpegString={setJpegString}
              resetQuestion={resetQuestion}
              setAnswerPhoto={setAnswerPhoto}
            />}


          {isQuestionLocked && thisQuestion && <Card>
            <div className={`flex flex-col items-center`}>
              <p className={`text-4xl`}>🔐</p>
              <h5 className={`font-righteous text-brandblue text-lg`}>{t('QuestionPages.QuestionPage.lockedQuestion')}</h5>
              <p className={``}>{lockedUntilText}</p>
            </div>
          </Card>}

          {!isQuestionLocked && thisQuestion && !showAnswerConfirmation &&
            <div className={`w-full mb-4`}>



              {showAiAnswerModal && <AiAnswer
                thisQuestion={thisQuestion}
                AiAnswer={questionAnswer && questionAnswer['AiAnswer']}
                setQuestionAnswerValue={setQuestionAnswerValue}
                validate={validate}
                setShowAiAnswerModal={setShowAiAnswerModal}
                questionAnswerValue={questionAnswerValue}
              />}

              {thisQuestion.QuestionFormat === 'YesOrNo' && <QuestionYesOrNo
                setQuestionAnswer={setQuestionAnswerValue}
                questionId={thisQuestion.Id}
                checklistId={checklistId}
                defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
              />}

              {thisQuestion.QuestionFormat === 'Text' && <QuestionText
                setQuestionAnswer={setQuestionAnswerValue}
                defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
              />}

              {thisQuestion.QuestionFormat === 'Location' && <QuestionLocation
                setQuestionAnswer={setQuestionAnswerValue}
                defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                questionId={thisQuestion.Id}
              />}

              {thisQuestion.QuestionFormat === 'Number' && <QuestionNumber
                setQuestionAnswer={setQuestionAnswerValue}
                questionAnswerValue={questionAnswerValue}
                questionId={thisQuestion.Id}
                defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
              />}

              {thisQuestion.QuestionFormat === 'MultipleTextFields' && <QuestionMultipleTextFields
                setQuestionAnswer={setQuestionAnswerValue}
                question={thisQuestion}
                questionId={thisQuestion.Id}
                checklistId={checklistId}
                handleChangeMultiText={handleChangeMultiText}
                defaultValue={questionAnswerValue}
                lastMultiTextFieldFromSpeech={lastMultiTextFieldFromSpeech}
              />}



              {thisQuestion.QuestionFormat === 'MultipleFields' && <QuestionMultipleFields
                question={thisQuestion}
                questionId={thisQuestion.Id}
                setQuestionAnswerValue={setQuestionAnswerValue}
              />}

              {thisQuestion.QuestionFormat === 'Photo' && <QuestionPhoto
                answerPhoto={answerPhoto}
                setAnswerPhoto={setAnswerPhoto}
                questionId={thisQuestion.Id}
                checklistId={checklistId}
                answerNote={answerNote}
                setAnswerNote={setAnswerNote}
                defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                jpegString={jpegString}
                setJpegString={setJpegString}
                setFooterButtons={setFooterButtons}
                isAnswerPending={isAnswerPending}
                thisQuestion={thisQuestion}
              />}

              {thisQuestion.QuestionFormat === 'Signature' && <QuestionSignature
                answerPhoto={answerPhoto}
                setAnswerPhoto={setAnswerPhoto}
                setFooterButtons={setFooterButtons}
                thisQuestion={thisQuestion}
              />}


              {
                thisQuestion.QuestionFormat === 'FridgeTemperature' &&
                <QuestionFridgeTemperature
                  thisQuestion={thisQuestion}
                  questionId={thisQuestion.Id}
                  checklistId={checklistId}
                  defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                  fridgeId={thisQuestion.FridgeId}
                  questionAnswer={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                  setQuestionAnswer={setQuestionAnswerValue}
                  answerNote={answerNote}
                  setAnswerNote={setAnswerNote}
                  answerPhoto={answerPhoto}
                  setAnswerPhoto={setAnswerPhoto}
                  isAnswerManuallyEdited={isAnswerManuallyEdited}
                  setIsAnswerManuallyEdited={setIsAnswerManuallyEdited}
                  jpegString={jpegString}
                  setJpegString={setJpegString}
                  fridgeHasDigitalDisplay={fridgeHasDigitalDisplay}
                  checkPeriodId={checkPeriodId}
                  setFooterButtons={setFooterButtons}
                  isAnswerPending={isAnswerPending}
                />}


              {
                thisQuestion.QuestionFormat === 'PhotoAndReading' &&
                <QuestionPhotoAndReading
                  thisQuestion={thisQuestion}
                  questionId={thisQuestion.Id}
                  checklistId={checklistId}
                  defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                  assetId={thisQuestion.AssetId}
                  questionAnswer={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                  setQuestionAnswer={setQuestionAnswerValue}
                  answerNote={answerNote}
                  setAnswerNote={setAnswerNote}
                  answerPhoto={answerPhoto}
                  setAnswerPhoto={setAnswerPhoto}
                  isAnswerManuallyEdited={isAnswerManuallyEdited}
                  setIsAnswerManuallyEdited={setIsAnswerManuallyEdited}
                  jpegString={jpegString}
                  setJpegString={setJpegString}
                  assetHasDigitalDisplay={false}
                  checkPeriodId={checkPeriodId}
                  setQuestionAnswerQRProof={setQuestionAnswerQRProof}
                  setFooterButtons={setFooterButtons}
                  footerButtons={footerButtons}
                />}

              {
                thisQuestion.QuestionFormat === 'AssetReading' &&
                <QuestionNumber
                  setQuestionAnswer={setQuestionAnswerValue}
                  questionId={thisQuestion.Id}
                  defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                />}

              {
                thisQuestion.QuestionFormat === 'Date' &&
                <QuestionDate
                  setQuestionAnswer={setQuestionAnswerValue}
                  questionId={thisQuestion.Id}
                  checklistId={checklistId}
                  defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                />}

              {
                thisQuestion.QuestionFormat === 'DateAndTime' &&
                <QuestionDateAndTime
                  setQuestionAnswer={setQuestionAnswerValue}
                  questionId={thisQuestion.Id}
                  checklistId={checklistId}
                  defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                />}

              {
                thisQuestion.QuestionFormat === 'Time' &&
                <QuestionTime
                  setQuestionAnswer={setQuestionAnswerValue}
                  questionId={thisQuestion.Id}
                  checklistId={checklistId}
                  defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                />}

              {thisQuestion.QuestionFormat === 'SelectMultiple' && <QuestionSelectMultiple
                handleChangeSelectMultiple={handleChangeSelectMultiple}
                questionId={thisQuestion.Id}
                checklistId={checklistId}
                defaultValue={questionAnswerValue !== null && questionAnswerValue instanceof Array ? questionAnswerValue : ''}
                questionOptions={thisQuestion.QuestionOptions}
              />}

              {thisQuestion.QuestionFormat === 'SelectOne' && <QuestionSelectOne
                setQuestionAnswer={setQuestionAnswerValue}
                questionId={thisQuestion.Id}
                checklistId={checklistId}
                defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                questionOptions={thisQuestion.QuestionOptions}
              />}



              {
                thisQuestion.QuestionFormat === 'CustomDataFormat'
                && regexString &&
                <QuestionCustomFormat
                  setQuestionAnswer={setQuestionAnswerValue}
                  questionId={thisQuestion.Id}
                  checklistId={checklistId}
                  defaultValue={questionAnswerValue !== null && !(questionAnswerValue instanceof Object) ? questionAnswerValue : ''}
                />}





            </div>}



          {/* <JsonDebug jsonobject={thisQuestion} /> */}


          {/* {errorMessage && <Modal
          // showCloseButton={true}
          // setShowModal={setErrorMessage}
          // closeButtonValue={null}
          >
            <div className={`flex flex-col gap-4`}>
              <RedAlert icon={true} size={'large'}>{errorMessage}</RedAlert>
              <Button
                onClick={() => setErrorMessage(null)}
                text={'Ok'}
                size={'big'}
              /></div>
          </Modal>} */}

        </div> :
        <div className={`max-w-3xl w-full p-5 flex flex-col gap-2 items-center justify-center text-center`}>
          <Spinner><p>{t('General.loading')}</p></Spinner>
        </div>
      }
    </div>





    <div className={`
  ${isVideoMode ? '' : ' border-t-2 border-gray-300 bg-white '} 
  fixed bottom-0 w-full flex flex-col items-center justify-center `}>

      {/* <p className={`text-xs text-center text-gray-300`}>{findNextQuestionUrl(answerId, checkPeriodId, tableData, '')}</p> */}


      {errorMessage && <div className={`max-w-3xl w-full px-5 pt-3 flex flex-row gap-2`}>
        <RedAlert fullwidth={true}>{errorMessage}</RedAlert>
      </div>}

      <div className={`max-w-3xl w-full px-5 py-3 flex flex-row gap-2`}>




        {footerButtons.includes('back') ? <Button
          internalLinkUrl={processIdForThisChecklist ? `/process/${processIdForThisChecklist}/` : `/checklists/${checkPeriodId}/`}
          text={t('Buttons.back')}
          disabled={false}
          variant={'primary'}
          ref={backButtonRef}
        /> : ''}

        {footerButtons.includes('skip') ? <Button
          onClick={() => saveAnswer('skip')}
          text={t('Buttons.skip')}
          disabled={false}
          variant={`primary`}
        /> : ''}

        {footerButtons.includes('takePhoto') ? <Button
          onClick={capturePhoto}
          text={t('Buttons.capture')}
          disabled={false}
          variant={'primary'}
        /> : ''}

        {footerButtons.includes('retakePhoto') ? <Button
          onClick={retakePhoto}
          text={t('Buttons.retake')}
          disabled={false}
          variant={'primary'}
        /> : ''}

        {footerButtons.includes('saveWithNote') ? <Button
          onClick={() => validate('confirm')}
          text={t('Buttons.saveWithNote')}
        /> : ''}

        {footerButtons.includes('save') ? <Button
          onClick={() => validate(null)}
          text={t('Buttons.save')}
          id={`saveButton`}
        /> : ''}

        {footerButtons.includes('next') ? <Button
          internalLinkUrl={nextStepUrl || '/checklists/'}
          text={t('Buttons.next')}
          disabled={false}
          variant={`primary`}
          ref={nextButtonRef}
        /> : ''}




      </div>
    </div>

  </div>
}
export default QuestionPage